import HttpService from '@/services/http';
import { toQuery } from '@/utils/infrastructure';
import request from '@/utils/infrastructure/communication/request';

/**
 * Fetch AI agents from the API
 *
 * Uses the access-aware management endpoint (`/ai/agent/manage`) so the
 * backend can return the right set of records based on the caller's role
 * — admins receive every agent in the tenant, per-agent collaborators
 * receive their share, regular users receive only what they can launch
 * — and expose the per-record `canManage` flag the UI uses to gate
 * edit/delete actions (see "AccessControl vs Collaborator" UX guide).
 *
 * @param {object} params - Query parameters (page, limit, search, etc.)
 * @returns {Promise} API response
 */
export async function fetchAIAgents(params = {}) {
  return HttpService.getInstance().request('/ai/agent/manage', {
    page: params.page || 1,
    limit: params.limit || 5,
    select: 'id,name,isActive,description,iconUrl,isPublic,aiAgentTeamId,canManage',
    orderBy: 'name',
    ...(params.search && {
      search: params.search,
      searchSelect: ['name', 'description'],
    }),
    ...(params.readPriority && { readPriority: true }),
    // Include filter parameter for OData filtering
    ...(params.filter && { filter: params.filter }),
    // Include other filter parameters (legacy support)
    ...(params.provider && { provider: params.provider }),
    ...(params.modelId && { modelId: params.modelId }),
    ...(params.memoryId && { memoryId: params.memoryId }),
    // Include other parameters that might be passed through
    ...Object.keys(params).reduce((acc, key) => {
      if (
        ![
          'page',
          'limit',
          'select',
          'orderBy',
          'search',
          'searchSelect',
          'readPriority',
          'filter',
          'provider',
          'modelId',
          'memoryId',
        ].includes(key)
      ) {
        acc[key] = params[key];
      }
      return acc;
    }, {}),
  });
}

/**
 * Fetch the catalog of active AI models (id + name) used by the in-header
 * model selector on the agent builder card. Mirrors the params the wizard's
 * `modelId` select uses (`ai/model/paged`, active only, ordered by name).
 * @param {object} params - Extra/override OData query parameters
 * @returns {Promise} API response ({ items, total })
 */
export async function fetchAIModels(params = {}) {
  return HttpService.getInstance().request('/ai/model/paged', {
    page: 1,
    limit: 200,
    select: 'id,name,provider',
    filter: 'isActive eq true',
    orderBy: 'name asc',
    ...params,
  });
}

/**
 * Fetch RPA agents from the API
 * @param {object} params - Query parameters (page, limit, search, etc.)
 * @returns {Promise} API response
 */
export async function fetchRPAAgents(params = {}) {
  return HttpService.getInstance().request('/rpa/api/agent/paged', {
    page: params.page || 1,
    limit: params.limit || 5,
    select: 'id,name,isActive,agentPlatform,created,updated',
    orderBy: 'name',
    ...(params.search && {
      search: params.search,
      searchSelect: ['name'],
    }),
    ...(params.readPriority && { readPriority: true }),
    ...(params.filter && { filter: params.filter }),
    ...(params.agentPlatform && { agentPlatform: params.agentPlatform }),
    ...Object.keys(params).reduce((acc, key) => {
      if (
        ![
          'page',
          'limit',
          'select',
          'orderBy',
          'search',
          'searchSelect',
          'readPriority',
          'filter',
          'agentPlatform',
          'status',
        ].includes(key)
      ) {
        acc[key] = params[key];
      }
      return acc;
    }, {}),
  });
}

/** Latest current session authority for a logical agent. 404 → unlinked. */
export async function getRpaAgentCurrentSession(agentId) {
  return HttpService.getInstance().request(`/rpa/designer/agent/${agentId}/session/current`, {
    suppressErrorNotification: true,
  });
}

/** Session history (array, not paged). take clamped server-side to 1..200. */
export async function fetchRpaAgentSessions(agentId, { skip = 0, take = 50 } = {}) {
  return HttpService.getInstance().request(`/rpa/designer/agent/${agentId}/sessions`, {
    passthroughParams: { skip, take },
    suppressErrorNotification: true,
  });
}

/** Revoke current session. 204; idempotent when already unlinked. */
export async function revokeRpaAgentSession(agentId) {
  return HttpService.getInstance().request(`/rpa/designer/agent/${agentId}/session/revoke`, {
    method: 'post',
  });
}

/** Create logical RPA agent. */
export async function createRPAAgent(data) {
  return HttpService.getInstance().request('/rpa/designer/agent', {
    method: 'post',
    data,
  });
}

/** Dashboard counters for an agent. */
export async function getRpaAgentDashboardSummary(agentId) {
  return HttpService.getInstance().request(`/rpa/designer/agent/${agentId}/dashboard-summary`, {
    suppressErrorNotification: true,
  });
}

/**
 * Get AI agent details by ID
 * @param {string} id - Agent ID
 * @param {object} params - Query parameters (select, expand, etc.)
 * @returns {Promise} API response
 */
export async function getAIAgentById(id, params) {
  return request(`/ai/agent/${id}`, {
    params: toQuery(params),
  });
}

/**
 * Get RPA agent details by ID
 * @param {string} id - Agent ID
 * @param {object} params - Query parameters (select, expand, etc.)
 * @returns {Promise} API response
 */
export async function getRPAAgentById(id, params) {
  return request(`/rpa/api/agent/${id}`, {
    params: toQuery(params),
  });
}

/**
 * Create a new AI agent
 * @param {object} data - AI agent data
 * @returns {Promise} API response
 */
export async function createAIAgent(data) {
  return request('/ai/agent', {
    method: 'POST',
    data,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Update an existing AI agent
 * @param {string} id - Agent ID
 * @param {object} data - AI agent data
 * @returns {Promise} API response
 */
export async function updateAIAgent(data) {
  return request(`/ai/agent`, {
    method: 'PUT',
    data,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Patch RPA agent name only (PATCH request with only name field)
 * @param {string} id - Agent ID
 * @param {object} data - Only name field: { name: string }
 * @returns {Promise} API response
 */
export async function patchRPAAgentName(id, data) {
  return request(`/rpa/designer/agent/${id}`, {
    method: 'PATCH',
    data,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Delete an AI agent
 * @param {string} id - Agent ID
 * @returns {Promise} API response
 */
export async function deleteAIAgent(id) {
  return request(`/ai/agent/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Delete an RPA agent
 * @param {string} id - Agent ID
 * @returns {Promise} API response
 */
export async function deleteRPAAgent(id) {
  return request(`/rpa/designer/agent/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Toggle AI agent active status
 * @param {string} id - Agent ID
 * @param {boolean} state - New active state
 * @returns {Promise} API response
 */
export async function toggleAIAgentActive(id, state) {
  return request(`/ai/agent/activate/${id}/${state}`, {
    method: 'POST',
  });
}

/**
 * Toggle RPA agent active status
 * @param {string} id - Agent ID
 * @param {boolean} state - New active state
 * @returns {Promise} API response
 */
export async function toggleRPAAgentActive(id, state) {
  return request(`/rpa/designer/agent/activate/${id}/${state}`, {
    method: 'POST',
  });
}

/**
 * Publish AI agent
 * @param {string} id - Agent ID
 * @returns {Promise} API response
 */
export async function publishAIAgent(id) {
  return request(`/ai/agent/publish/${id}`, {
    method: 'POST',
  });
}

/**
 * Restore deleted AI agent(s)
 * @param {string|string[]} agentIds - Agent ID or array of IDs
 * @returns {Promise} API response
 */
export async function restoreAIAgent(agentIds) {
  return request('/ai/agent/restore', {
    method: 'POST',
    data: Array.isArray(agentIds) ? agentIds : [agentIds],
    headers: { 'Content-Type': 'application/json' },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Agent versioned configuration (token-based builder).
//
// Mirrors the customList/app config flow:
//   1) generateToken → { aiAgentId, versionId, versionNumber, mode, token }
//   2) version       → returns AIAgentVersion config snapshot for that id
//   3) append/modify/remove → mutate the draft version through elementType
//   4) publish       → promote version, sync mirror fields on AIAgent, side-effects
//   5) setCurrent    → point the agent at a previously published version
//   6) versions/traces/dependencies → audit and impact analysis
//
// All routes live under `ai/agent/config` and require the encrypted token
// returned by generateToken to be passed as a query-string parameter.
// ─────────────────────────────────────────────────────────────────────────────

// All versioned-config wrappers must go through HttpService, NOT the raw
// `request` helper. HttpService preserves the `token` query param (it's
// whitelisted under ENTITY_ID_FIELDS); the raw helper would otherwise feed
// the params through `toQuery`, which only emits OData-shaped keys and would
// silently drop the token, sending unauthenticated requests to the backend.
// This mirrors the listBuilder / appBuilder pattern.

/**
 * Request a builder token for an AI agent version.
 * @param {string} aiAgentId
 * @param {number|string} mode - CoreEnums.AccessMode.Write | ReadOnly
 * @param {string} [versionId] - Optional: when omitted, backend resolves the
 *   latest version (creating a new draft if the latest is published).
 */
export async function generateAIAgentConfigToken(aiAgentId, mode, versionId) {
  const fragments = [aiAgentId, mode, versionId]
    .filter((v) => v !== undefined && v !== null && v !== '')
    .join('/');
  return HttpService.getInstance().request(`/ai/agent/config/generateToken/${fragments}`, {
    method: 'post',
  });
}

/**
 * Read the configuration snapshot of a given AI agent version.
 */
export async function getAIAgentVersionConfig(aiAgentId, versionId, token) {
  return HttpService.getInstance().request(`/ai/agent/config/version/${aiAgentId}/${versionId}`, {
    params: { token },
  });
}

/**
 * Append an element to the AI agent version configuration.
 * Use for collection-style additions (greetings, training vectors,
 * permissions entries, etc.) — see AIAgentBuilderElementType enum.
 */
export async function appendAIAgentConfigElement(aiAgentId, versionId, token, body) {
  return HttpService.getInstance().request(`/ai/agent/config/append/${aiAgentId}/${versionId}`, {
    method: 'post',
    params: { token },
    data: body,
  });
}

/**
 * Modify an existing element of the AI agent version configuration.
 * `body.itemId` (and optionally `itemKey`) identifies the entry to update.
 */
export async function modifyAIAgentConfigElement(aiAgentId, versionId, token, body) {
  return HttpService.getInstance().request(`/ai/agent/config/modify/${aiAgentId}/${versionId}`, {
    method: 'post',
    params: { token },
    data: body,
  });
}

/**
 * Remove an element from the AI agent version configuration.
 * `body.itemId` (and optionally `itemKey`, `force`) target the entry.
 */
export async function removeAIAgentConfigElement(aiAgentId, versionId, token, body) {
  return HttpService.getInstance().request(`/ai/agent/config/remove/${aiAgentId}/${versionId}`, {
    method: 'post',
    params: { token },
    data: body,
  });
}

/**
 * Publish a draft version of the AI agent. Backend marks the version as
 * published, switches the current pointer, syncs mirror fields on AIAgent
 * (name/email/modelId/memoryId), and triggers side-effects (OpenAI vector
 * store resync, WhatsApp subscriptions, etc.).
 *
 * Returns an `AIAgentPublishResult` ({ aiAgentName, version, published,
 * token, inconsistencies }). When the auto-generated internal summary fails
 * validation the backend responds with `published: false` plus a list of
 * `inconsistencies`; callers can retry with `force: true` to publish anyway.
 * Structural validation errors (config.Validate) still surface as HTTP 4xx
 * and cannot be bypassed with `force`.
 *
 * @param {string} aiAgentId
 * @param {string} versionId
 * @param {string} token - Write token from the AIAgentConfigurationToken.
 * @param {boolean} [force=false] - Publish despite generated-summary warnings.
 */
export async function publishAIAgentVersion(aiAgentId, versionId, token, force = false) {
  // `force` is appended straight to the URL: HttpService filters `params`
  // through `toQuery` + the ENTITY_ID_FIELDS whitelist (which only keeps OData
  // keys and `token`), so a `force` param would be silently dropped and never
  // reach the backend. The token still rides in `params` (it IS whitelisted).
  const query = force ? '?force=true' : '';
  return HttpService.getInstance().request(
    `/ai/agent/config/publish/${aiAgentId}/${versionId}${query}`,
    {
      method: 'post',
      params: { token },
    },
  );
}

/**
 * Point the agent at a previously published version (ChangeCurrentVersion).
 * Mirrors `POST app/config/setCurrent/{appId}/{versionId}` — does not require
 * a write token because the version is already published.
 */
export async function setCurrentAIAgentVersion(aiAgentId, versionId, token) {
  return HttpService.getInstance().request(
    `/ai/agent/config/setCurrent/${aiAgentId}/${versionId}`,
    {
      method: 'post',
      params: token ? { token } : {},
    },
  );
}

/**
 * List versions of an AI agent (OData-paginated).
 * @param {string} aiAgentId
 * @param {object} [params] - Standard OData params (select/orderBy/limit/page)
 */
export async function getAIAgentVersions(aiAgentId, params = {}) {
  const { readPriority, ...query } = params || {};
  return HttpService.getInstance().request(`/ai/agent/config/${aiAgentId}/versions`, {
    params: {
      select: 'id,aiAgentId,number,current,published,publisher,publishedDate,creator,created',
      orderBy: 'number desc',
      limit: 50,
      ...query,
    },
    ...(readPriority && { readPriority: true }),
  });
}

/**
 * Audit log for the AI agent configuration (version-scoped when versionId given).
 */
export async function getAIAgentConfigTraces(aiAgentId, versionId, params = {}) {
  const route = versionId
    ? `/ai/agent/config/traces/${aiAgentId}/${versionId}`
    : `/ai/agent/config/traces/${aiAgentId}`;
  return HttpService.getInstance().request(route, { params });
}

/**
 * Reverse-dependency report (impact analysis) for the AI agent configuration.
 * Token is required because dependencies expose data scoped to the working version.
 */
export async function getAIAgentConfigDependencies(aiAgentId, versionId, token, params = {}) {
  const route = versionId
    ? `/ai/agent/config/${aiAgentId}/dependencies/${versionId}`
    : `/ai/agent/config/${aiAgentId}/dependencies`;
  return HttpService.getInstance().request(route, { params: { ...params, token } });
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Agent collaborators
//
// Backend contract (see the "AccessControl vs Collaborator" UX guide):
//   GET    /ai/agent/config/collaborators/{aiAgentId}
//   POST   /ai/agent/config/collaborators/{aiAgentId}  body: { collaboratorId }
//   DELETE /ai/agent/config/collaborators/{aiAgentId}/{collaboratorRecordId}
//
// These routes are NOT version-scoped — collaborators live on the AIAgent
// entity itself, not the draft AIAgentVersion. We still use HttpService so
// the request follows the same auth pipeline as the rest of the builder.
// ─────────────────────────────────────────────────────────────────────────────

export async function getAIAgentCollaborators(aiAgentId, { readPriority = false } = {}) {
  return HttpService.getInstance().request(`/ai/agent/config/collaborators/${aiAgentId}`, {
    // `readPriority: true` adds the `AUTOMY-Read-Priority: primary`
    // header so the GET hits the primary replica — avoids stale rows
    // from a lagging read replica right after a POST/DELETE.
    ...(readPriority && { readPriority: true }),
  });
}

export async function addAIAgentCollaborator(aiAgentId, collaboratorId) {
  return HttpService.getInstance().request(`/ai/agent/config/collaborators/${aiAgentId}`, {
    method: 'post',
    data: { collaboratorId },
  });
}

export async function removeAIAgentCollaborator(aiAgentId, collaboratorRecordId) {
  return HttpService.getInstance().request(
    `/ai/agent/config/collaborators/${aiAgentId}/${collaboratorRecordId}`,
    { method: 'delete' },
  );
}
